/* eslint-disable no-plusplus */
import { message } from 'antd';
import React from 'react';
import { isIOS } from 'react-device-detect';
import { getAccessToken } from './authorization';
import { API_HOST } from './variables';

export const parseJSON = (jsonString, fallback = {}) => {
  if (typeof jsonString === 'object') {
    return jsonString;
  }
  try {
    return JSON.parse(jsonString);
  } catch (error) {
    console.error(jsonString);
    console.error(error.message);
    return fallback;
  }
};

export const validatePassword = (value) => {
  const passwordRegex = /^(?=.*[a-zA-Z])(?=.*\d)(?=.*[^a-zA-Z0-9]).{8,}$/;

  return passwordRegex.test(value);
};

export const escapeRegExp = (str) => str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

export const jsonPrettyStringify = (json) => {
  let result = json;
  if (typeof result !== 'string') {
    result = JSON.stringify(result, undefined, 2);
  }
  result = result.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

  return result.replace(
    /("(\\u[a-zA-Z0-9]{4}|\\[^u]|[^\\"])*"(\s*:)?|\b(true|false|null)\b|-?\d+(?:\.\d*)?(?:[eE][+-]?\d+)?)/g,
    (match) => {
      let cls = 'number';
      if (/^"/.test(match)) {
        if (/:$/.test(match)) {
          cls = 'key';
        } else {
          cls = 'string';
        }
      } else if (/true|false/.test(match)) {
        cls = 'boolean';
      } else if (/null/.test(match)) {
        cls = 'null';
      }
      return `<span class="${cls}">${match}</span>`;
    },
  );
};

export const getTimeCodeFromNum = (num) => {
  let seconds = parseInt(num, 10);
  let minutes = parseInt((seconds / 60), 10);
  seconds -= minutes * 60;
  const hours = parseInt((minutes / 60), 10);
  minutes -= hours * 60;

  if (hours === 0) return `${minutes}:${String(seconds % 60).padStart(2, 0)}`;
  return `${String(hours).padStart(2, 0)}:${minutes}:${String(
    seconds % 60,
  ).padStart(2, 0)}`;
};

export const isReactNativeWebView = () => !!window.isRNWebView;

/**
 * iOS 앱스토어 심사 모드 여부를 확인하는 함수
 * 환경변수 REACT_APP_IOS_REVIEW_MODE=true 이고, iOS 앱 내부(React Native WebView)일 때만 true 반환
 *
 * 사용법:
 *   - 심사 제출 시: .env 또는 CI 환경변수에서 REACT_APP_IOS_REVIEW_MODE=true 설정 후 빌드
 *   - 심사 완료 후: REACT_APP_IOS_REVIEW_MODE=false 설정 후 빌드 (코드 수정 불필요)
 */
export const isIOSReviewMode = () => (
  process.env.REACT_APP_IOS_REVIEW_MODE === 'true' && isIOS && !!window.isRNWebView
);

export const copyText = (text, onSuccess) => {
  navigator.clipboard.writeText(text)
    .then(() => { if (onSuccess) onSuccess(); })
    .catch(console.error);
};

export const shareUrl = (title, url) => {
  // if (isReactNativeWebView()) {
  //   url = `ploooto:${url.substring(window.location.protocol.length)}`;
  // }

  if (window.ReactNativeWebView) {
    window.ReactNativeWebView.postMessage(`SHARE_${title}\n\n${url}`);
  } else if (navigator.share) {
    navigator.share({
      title,
      url,
    }).then(() => {
      console.log('Thanks for sharing!');
      message.info('공유에 성공했습니다.');
    }).catch((err) => {
      message.error('공유에 실패했습니다.');
      console.error(err);
    });
  } else {
    copyText(url, (() => { message.info('URL을 클립보드에 복사했습니다.'); }));
  }
};

export const dataURLtoFile = (dataurl, fileName) => {
  const arr = dataurl.split(',');
  const mime = arr[0].match(/:(.*?);/)[1];
  const bstr = atob(arr[1]);
  let num = bstr.length;
  const u8arr = new Uint8Array(num);

  // eslint-disable-next-line no-const-assign
  while (num--) {
    u8arr[num] = bstr.charCodeAt(num);
  }
  return new File([u8arr], fileName, { type: mime });
};

export const getUserProfilePageUrl = (id) => `/p/${id}`;
export const getMinibookUrl = (id) => `/collection/preview/${id}`;

export const parseLinkTextToHTML = (text) => {
  const regURL = new RegExp('(http|https|ftp|telnet|news|irc)://([-/.a-zA-Z0-9_~#%$?&=:200-377]+)', 'gi');
  const regEmail = new RegExp('([xA1-xFEa-z0-9_-]+@[xA1-xFEa-z0-9-]+.[a-z0-9-]+)', 'gi');

  return text
    .replace(regURL, "<a href='$1://$2' target='_blank'>$1://$2</a>")
    .replace(regEmail, "<a href='mailto:$1'>$1</a>");
};
export const parseLinkTextToReactElements = (text) => {
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  const emailRegex = /([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/g;

  const splitByURL = text.split(urlRegex).flatMap((segment) => segment.split(emailRegex));

  return splitByURL.map((part) => {
    const key = `key-${part}-${Math.random().toString(36).substring(2, 8)}`; // 간단한 랜덤 키 생성

    if (urlRegex.test(part)) {
      return (
        <a key={`url-${key}`} href={part} target="_blank" rel="noopener noreferrer">
          {part}
        </a>
      );
    } if (emailRegex.test(part)) {
      return (
        <a key={`email-${key}`} href={`mailto:${part}`}>
          {part}
        </a>
      );
    }
    return <React.Fragment key={`text-${key}`}>{part}</React.Fragment>;
  });
};

const numbering = (value, division) => {
  const result = Math.floor(value / division);
  return result === 0 ? null : (result % division);
};

const NUMBER_FORMAT_REGX = /\B(?=(\d{3})+(?!\d))/g;
const numberFormat = (value) => value.toString().replace(NUMBER_FORMAT_REGX, ',');
const setUnitText = (numbers) => {
  const unit = ['원', '만', '억', '조', '경'];
  return numbers.map((number, index) => (number ? numberFormat(number) + unit[(unit.length - 1) - index] : number));
};

export const getMoneyFormat = (value) => {
  const numbers = [
    numbering(value % 100000000000000000000, 10000000000000000),
    numbering(value % 10000000000000000, 1000000000000),
    numbering(value % 1000000000000, 100000000),
    numbering(value % 100000000, 10000),
    value % 10000,
  ];

  return setUnitText(numbers)
    .filter((number) => !!number)
    .join(' ');
};

export async function OpenAIStream(searchBotInput, userLanguage) {
  const payload = {
    model: 'gpt-4o-search-preview-2025-03-11',
    messages: [
      {
        role: 'user',
        content:
          '- 너는 YouBook 에서 활동하는 책 읽는 고양이야. 앞으로 너에 대해 물어보면 그렇게 대답해줘.'
          + '\n- 답변을 할 때에는 친근한 말투로 상담하듯이 해줘.'
          + '\n- 답변에 URL이나 링크는 절대 포함하지 말고, 텍스트로만 설명해줘.'
          + `\n- 질문 언어에 상관없이 대답은 항상 다음 언어로 해줘: "${userLanguage}"`
          + `\n\n질문: ${searchBotInput}`,
      },
    ],
    max_tokens: 1050,
    // temperature: 0.1,
    stream: true,
  };

  const completion = await fetch(`${API_HOST}/v1/private/ai/chat/stream`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${getAccessToken()}`,
    },
    body: JSON.stringify(payload),
  });

  // Create an EventSource-like object to process the SSE stream
  const eventSource = new EventTarget();
  let buffer = '';
  const decoder = new TextDecoder();

  async function processStream() {
    const reader = completion.body.getReader();
    // eslint-disable-next-line no-constant-condition
    while (true) {
      // eslint-disable-next-line no-await-in-loop
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      let newlineIndex;
      // eslint-disable-next-line no-cond-assign
      while ((newlineIndex = buffer.indexOf('\n')) !== -1) {
        const line = buffer.slice(0, newlineIndex);
        buffer = buffer.slice(newlineIndex + 1);

        if (line.startsWith('data: ')) {
          const data = line.slice(6);
          if (data === '[DONE]') {
            eventSource.dispatchEvent(new Event('done'));
          } else {
            eventSource.dispatchEvent(new CustomEvent('message', { detail: data }));
          }
        }
      }
    }
  }

  processStream().catch((error) => {
    console.error('Error processing SSE stream:', error);
    eventSource.dispatchEvent(new CustomEvent('error', { detail: error }));
  });

  return eventSource;
}

/**
 * Html String 에서 Text 만 추출.
 * TTS 를 잘 하기위해, 단락마다 '. ' 을 추가함.
 */
export function extractTextFromHTML(htmlString) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlString, 'text/html');
  const { body } = doc;

  function addDotToTextNode(node) {
    if (node.nodeType === 3) { // 3은 텍스트 노드를 의미
      const text = node.textContent.trim();
      if (text && !text.endsWith('.')) {
        // eslint-disable-next-line no-param-reassign
        node.textContent += '. ';
      }
    }
    Array.from(node.childNodes).forEach(addDotToTextNode);
  }

  addDotToTextNode(body);
  return body.textContent.trim();
}

const languageMap = {
  ar: 'Arabic',
  bn: 'Bengali',
  ca: 'Catalan',
  cs: 'Czech',
  da: 'Danish',
  de: 'German',
  el: 'Greek',
  en: 'English',
  es: 'Spanish',
  et: 'Estonian',
  fa: 'Persian',
  fi: 'Finnish',
  fil: 'Filipino',
  fr: 'French',
  ga: 'Irish',
  he: 'Hebrew',
  hi: 'Hindi',
  hr: 'Croatian',
  hu: 'Hungarian',
  id: 'Indonesian',
  it: 'Italian',
  ja: 'Japanese',
  ko: 'Korean',
  la: 'Latin',
  lt: 'Lithuanian',
  lv: 'Latvian',
  ms: 'Malay',
  nl: 'Dutch',
  no: 'Norwegian',
  pl: 'Polish',
  pt: 'Portuguese',
  ro: 'Romanian',
  ru: 'Russian',
  sk: 'Slovak',
  sl: 'Slovenian',
  sv: 'Swedish',
  ta: 'Tamil',
  th: 'Thai',
  tr: 'Turkish',
  uk: 'Ukrainian',
  ur: 'Urdu',
  vi: 'Vietnamese',
  zh: 'Chinese',
};

export function getLanguage(lan) {
  return languageMap[lan] || 'Unknown Language';
}

/**
 * 전화번호를 한국 형식으로 포맷팅합니다.
 * @param {string} phone - 포맷팅할 전화번호
 * @returns {string} 포맷팅된 전화번호 (예: 010-1234-5678, 02-1234-5678)
 */
export const formatPhoneNumber = (phone) => {
  if (!phone) return '';

  // 숫자만 추출
  const numbers = phone.replace(/[^0-9]/g, '');

  // 빈 문자열이면 그대로 반환
  if (!numbers) return phone;

  // 국제번호 제거 (82로 시작하는 경우)
  let cleaned = numbers;
  if (cleaned.startsWith('82')) {
    cleaned = `0${cleaned.substring(2)}`;
  }

  // 휴대폰 번호 (010, 011, 016, 017, 018, 019)
  if (cleaned.length === 11 && cleaned.startsWith('010')) {
    return `${cleaned.substring(0, 3)}-${cleaned.substring(3, 7)}-${cleaned.substring(7)}`;
  }
  if (cleaned.length === 11 && (cleaned.startsWith('011') || cleaned.startsWith('016') || cleaned.startsWith('017') || cleaned.startsWith('018') || cleaned.startsWith('019'))) {
    return `${cleaned.substring(0, 3)}-${cleaned.substring(3, 7)}-${cleaned.substring(7)}`;
  }

  // 서울 지역번호 (02)
  if (cleaned.length === 9 || cleaned.length === 10) {
    if (cleaned.startsWith('02')) {
      if (cleaned.length === 9) {
        return `${cleaned.substring(0, 2)}-${cleaned.substring(2, 5)}-${cleaned.substring(5)}`;
      }
      return `${cleaned.substring(0, 2)}-${cleaned.substring(2, 6)}-${cleaned.substring(6)}`;
    }
  }

  // 기타 지역번호 (031, 032, 033, 041, 042, 043, 044, 051, 052, 053, 054, 055, 061, 062, 063, 064)
  if (cleaned.length === 10 || cleaned.length === 11) {
    if (cleaned.startsWith('0')) {
      const areaCode = cleaned.substring(0, 3);
      if (cleaned.length === 10) {
        return `${areaCode}-${cleaned.substring(3, 6)}-${cleaned.substring(6)}`;
      }
      return `${areaCode}-${cleaned.substring(3, 7)}-${cleaned.substring(7)}`;
    }
  }

  // 포맷팅이 안 되는 경우 원본 반환
  return phone;
};
